'use client';

import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

// --- Hooks ---
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Lib ---
import { isExtensionAvailable, isMarkInSchema, isNodeTypeSelected } from '@/lib/tiptap-utils';

// --- Icons ---
import { HighlighterIcon } from '@/components/tiptap-editor/tiptap-icons/highlighter-icon';

export const COLOR_HIGHLIGHT_SHORTCUT_KEY = 'mod+shift+h';
export const HIGHLIGHT_COLORS = [
    {
        label: 'Default background',
        value: 'rgba(255, 255, 255, 1)',
        border: 'rgba(37, 39, 45, 0.1)',
    },
    {
        label: 'Gray background',
        value: 'rgb(248, 248, 247)',
        border: 'rgba(84, 72, 49, 0.15)',
    },
    {
        label: 'Brown background',
        value: 'rgb(244, 238, 238)',
        border: 'rgba(210, 162, 141, 0.35)',
    },
    {
        label: 'Orange background',
        value: 'rgb(251, 236, 221)',
        border: 'rgba(224, 124, 57, 0.27)',
    },
    {
        label: 'Yellow background',
        value: '#fef9c3',
        border: '#fbe604',
    },
    {
        label: 'Green background',
        value: '#dcfce7',
        border: '#c7fad8',
    },
    {
        label: 'Blue background',
        value: '#e0f2fe',
        border: '#ceeafd',
    },
    {
        label: 'Purple background',
        value: '#f3e8ff',
        border: '#e4ccff',
    },
    {
        label: 'Pink background',
        value: 'rgb(252, 241, 246)',
        border: 'rgba(225, 136, 179, 0.27)',
    },
    {
        label: 'Red background',
        value: '#ffe4e6',
        border: '#ffccd0',
    },
];
export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

export type HighlightMode = 'mark' | 'node';

/**
 * Configuration for the color highlight functionality
 */
export interface UseColorHighlightConfig {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor | null;
    /**
     * The color to apply when toggling the highlight.
     */
    highlightColor?: string;
    /**
     * Optional label to display alongside the icon.
     */
    label?: string;
    /**
     * Whether the button should hide when the mark is not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
    /**
     * The highlighting mode to use.
     * - "mark": Uses the highlight mark extension (default)
     * - "node": Uses the node background extension
     * @default "mark"
     */
    mode?: HighlightMode;
    /**
     * Called when the highlight is applied.
     */
    onApplied?: ({ color, label, mode }: { color: string; label: string; mode: HighlightMode }) => void;
}

export function pickHighlightColorsByValue(values: string[]) {
    const colorMap = new Map(HIGHLIGHT_COLORS.map(color => [color.value, color]));
    return values.map(value => colorMap.get(value)).filter((color): color is (typeof HIGHLIGHT_COLORS)[number] => !!color);
}

/**
 * Checks if highlight can be applied based on the mode and current editor state
 */
export function canColorHighlight(editor: Editor | null, mode: HighlightMode = 'mark'): boolean {
    if (!editor || !editor.isEditable) return false;

    if (mode === 'mark') {
        if (!isMarkInSchema('highlight', editor) || isNodeTypeSelected(editor, ['image'])) return false;

        return editor.can().setMark('highlight');
    } else {
        if (!isExtensionAvailable(editor, ['nodeBackground'])) return false;

        try {
            return editor.can().toggleNodeBackgroundColor('test');
        } catch {
            return false;
        }
    }
}

/**
 * Checks if highlight is currently active
 */
export function isColorHighlightActive(editor: Editor | null, highlightColor?: string, mode: HighlightMode = 'mark'): boolean {
    if (!editor || !editor.isEditable) return false;

    if (mode === 'mark') {
        return highlightColor ? editor.isActive('highlight', { color: highlightColor }) : editor.isActive('highlight');
    } else {
        if (!highlightColor) return false;

        try {
            const { state } = editor;
            const { selection } = state;

            const $pos = selection.$anchor;
            for (let depth = $pos.depth; depth >= 0; depth--) {
                const node = $pos.node(depth);
                if (node && node.attrs?.backgroundColor === highlightColor) {
                    return true;
                }
            }
            return false;
        } catch {
            return false;
        }
    }
}

/**
 * Removes highlight based on the mode
 */
export function removeHighlight(editor: Editor | null, mode: HighlightMode = 'mark'): boolean {
    if (!editor || !editor.isEditable) return false;
    if (!canColorHighlight(editor, mode)) return false;

    if (mode === 'mark') {
        return editor.chain().focus().unsetMark('highlight').run();
    } else {
        return editor.chain().focus().unsetNodeBackgroundColor().run();
    }
}

/**
 * Determines if the highlight button should be shown
 */
export function shouldShowButton(props: { editor: Editor | null; hideWhenUnavailable: boolean; mode: HighlightMode }): boolean {
    const { editor, hideWhenUnavailable, mode } = props;

    if (!editor || !editor.isEditable) return false;

    if (mode === 'mark') {
        if (!isMarkInSchema('highlight', editor)) return false;
    } else {
        if (!isExtensionAvailable(editor, ['nodeBackground'])) return false;
    }

    if (hideWhenUnavailable && !editor.isActive('code')) {
        return canColorHighlight(editor, mode);
    }

    return true;
}

type ColorHighlightToolbarSnapshot = {
    isVisible: boolean;
    isActive: boolean;
    canColorHighlight: boolean;
};

function colorHighlightToolbarSnapshotEquality(a: ColorHighlightToolbarSnapshot, b: ColorHighlightToolbarSnapshot | null): boolean {
    return a.isVisible === b?.isVisible && a.isActive === b?.isActive && a.canColorHighlight === b?.canColorHighlight;
}

export function useColorHighlight(config: UseColorHighlightConfig) {
    const { editor: providedEditor, label, highlightColor, hideWhenUnavailable = false, mode = 'mark', onApplied } = config;

    const { editor } = useTiptapEditor(providedEditor);
    const isMobile = useIsBreakpoint();

    const colorHighlightToolbarSelector = useCallback(
        (ctx: EditorStateSnapshot<Editor | null>): ColorHighlightToolbarSnapshot => {
            const ed = ctx.editor;
            if (!ed) {
                return { isVisible: false, isActive: false, canColorHighlight: false };
            }
            return {
                isVisible: shouldShowButton({ editor: ed, hideWhenUnavailable, mode }),
                isActive: isColorHighlightActive(ed, highlightColor, mode),
                canColorHighlight: canColorHighlight(ed, mode),
            };
        },
        [hideWhenUnavailable, mode, highlightColor],
    );

    const colorHighlightToolbar = useEditorState({
        editor,
        selector: colorHighlightToolbarSelector,
        equalityFn: colorHighlightToolbarSnapshotEquality,
    });

    const isVisible = colorHighlightToolbar?.isVisible ?? false;
    const isActive = colorHighlightToolbar?.isActive ?? false;
    const canColorHighlightState = colorHighlightToolbar?.canColorHighlight ?? false;

    const handleColorHighlight = useCallback(() => {
        if (!editor || !canColorHighlight(editor, mode) || !highlightColor || !label) return false;

        if (mode === 'mark') {
            if (editor.state.storedMarks) {
                const highlightMarkType = editor.schema.marks.highlight;
                if (highlightMarkType) {
                    editor.view.dispatch(editor.state.tr.removeStoredMark(highlightMarkType));
                }
            }

            setTimeout(() => {
                const success = editor.chain().focus().toggleMark('highlight', { color: highlightColor }).run();
                if (success) {
                    onApplied?.({ color: highlightColor, label, mode });
                }
                return success;
            }, 0);

            return true;
        } else {
            const success = editor.chain().focus().toggleNodeBackgroundColor(highlightColor).run();

            if (success) {
                onApplied?.({ color: highlightColor, label, mode });
            }
            return success;
        }
    }, [highlightColor, editor, label, onApplied, mode]);

    const handleRemoveHighlight = useCallback(() => {
        const success = removeHighlight(editor, mode);
        if (success) {
            onApplied?.({ color: '', label: 'Remove highlight', mode });
        }
        return success;
    }, [editor, onApplied, mode]);

    useHotkeys(
        COLOR_HIGHLIGHT_SHORTCUT_KEY,
        event => {
            event.preventDefault();
            handleColorHighlight();
        },
        {
            enabled: isVisible && canColorHighlightState,
            enableOnContentEditable: !isMobile,
            enableOnFormTags: true,
        },
    );

    return {
        isVisible,
        isActive,
        handleColorHighlight,
        handleRemoveHighlight,
        canColorHighlight: canColorHighlightState,
        label: label || `Highlight`,
        shortcutKeys: COLOR_HIGHLIGHT_SHORTCUT_KEY,
        Icon: HighlighterIcon,
        mode,
    };
}
